var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Type as ModelOutputType } from '@augloop/interfaces';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
export function lackingPromiseOrWasmSupport(logOp) {
    let errMsg = '';
    if (typeof navigator != 'undefined' && navigator.userAgent) {
        const ua = navigator.userAgent;
        if (!ua.indexOf || ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0) {
            // Exclude IE due to lack of support
            errMsg = 'Error: IE lacks necessary support';
        }
        else if (typeof Promise == 'undefined') {
            errMsg = 'Error: Promise is not supported';
        }
        else if (typeof WebAssembly == 'undefined') {
            errMsg = 'Error: WASM is not supported';
        }
    }
    else {
        errMsg = 'Error: No window userAgent';
    }
    if (errMsg == '') {
        return false;
    }
    else {
        logOp.resultDescription = errMsg;
        Log.info(0x2035f416 /* tag_6n5qw */, LogCategory.CoreDefault, logOp.stop());
        return true;
    }
}
export class Model {
    constructor(session, modelName, onnxObject) {
        this.session = session;
        this.onnxObject = onnxObject;
        this.modelName = modelName;
    }
    /* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
    static create(modelUrl, modelName, onnxObject) {
        return __awaiter(this, void 0, void 0, function* () {
            const createModelOp = new LogOperation({
                operationName: 'CreateModelForInferencing',
                success: true,
                resourceId: modelName
            });
            createModelOp.start();
            if (onnxObject[modelUrl]) {
                createModelOp.resultDescription = 'Model already existed';
                Log.info(0x1e616684 /* tag_4yw0e */, LogCategory.CoreDefault, createModelOp.stop());
                return onnxObject[modelUrl];
            }
            else {
                return onnxObject[modelUrl] = this.getInferenceSession(onnxObject).create(modelUrl).then((session) => {
                    Log.info(0x2035f417 /* tag_6n5qx */, LogCategory.CoreDefault, createModelOp.stop());
                    return new Model(session, modelName, onnxObject);
                }).catch((err) => {
                    createModelOp.resultDescription = `Error: ${err}`;
                    Log.info(0x2035f418 /* tag_6n5qy */, LogCategory.CoreDefault, createModelOp.stop());
                    throw err;
                });
            }
        });
    }
    static getInferenceSession(onnxObject) {
        if (onnxObject === null || onnxObject === void 0 ? void 0 : onnxObject.InferenceSession) {
            return onnxObject.InferenceSession;
        }
        else {
            throw new Error('InferenceSession is not defined');
        }
    }
    run(inputs) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const runModelOp = new LogOperation({
                operationName: 'RunModelForInferencing',
                success: true,
                resourceId: this.modelName
            });
            runModelOp.start();
            if ((_a = this.onnxObject) === null || _a === void 0 ? void 0 : _a.Tensor) {
                // translate inputs
                const ogInputs = inputs.getInputs();
                const tensorInputs = {};
                Object.keys(ogInputs).forEach((inputName2) => {
                    const input = ogInputs[inputName2];
                    if (Array.isArray(input.values) && input.values.length > 0 && typeof (input.values[0]) === 'boolean') {
                        const uint8sFromBoolValues = new Uint8Array(input.values);
                        tensorInputs[inputName2] = new this.onnxObject.Tensor(uint8sFromBoolValues, input.dimensions);
                    }
                    else {
                        tensorInputs[inputName2] = new this.onnxObject.Tensor(input.values, input.dimensions);
                    }
                });
                return this.session.run(tensorInputs).then((output) => {
                    Log.info(0x2035f419 /* tag_6n5qz */, LogCategory.CoreDefault, runModelOp.stop());
                    return new Outputs(output);
                }).catch((err) => {
                    runModelOp.resultDescription = `Problem running model. ${err.message}`;
                    Log.info(0x2035f41a /* tag_6n5q0 */, LogCategory.CoreDefault, runModelOp.stop());
                });
            }
            else {
                runModelOp.resultDescription = `Unable to run model. Tensor not defined`;
                Log.info(0x2035f41b /* tag_6n5q1 */, LogCategory.CoreDefault, runModelOp.stop());
                throw new Error('Tensor is not defined');
            }
        });
    }
}
export class Inputs {
    constructor() {
        this.map = {};
    }
    static create() {
        return new Inputs();
    }
    getInputs() {
        return this.map;
    }
    add(name, dimensions, values) {
        this.map[name] = { dimensions: dimensions, values: values };
        return this;
    }
}
class Outputs {
    constructor(outputs) {
        for (const name of Object.keys(outputs)) {
            this[name] = new OutputView(name, outputs[name]);
        }
    }
}
class OutputView {
    constructor(name, outputTensor) {
        this.resultName = name;
        this.result = outputTensor;
    }
    name() {
        return this.resultName;
    }
    outputType() {
        switch (this.result.type) {
            case 'string':
                return ModelOutputType.Strings;
            case 'int8':
                return ModelOutputType.Int8s;
            case 'int16':
                return ModelOutputType.Int16s;
            case 'int32':
                return ModelOutputType.Int32s;
            case 'bool':
                return ModelOutputType.Booleans;
            case 'uint8':
                return ModelOutputType.UnsignedInt8s;
            case 'uint16':
                return ModelOutputType.UnsignedInt16s;
            case 'uint32':
                return ModelOutputType.UnsignedInt32s;
            case 'float32':
                return ModelOutputType.Float32s;
            case 'float64':
                return ModelOutputType.Float64s;
            default:
                throw new Error(`The output type ${this.result.type} is not supported.`);
        }
    }
    dimensions() {
        return this.result.dims;
    }
    asBooleans() {
        if (this.outputType() === ModelOutputType.Booleans) {
            return this.result.data.map(value => !!value);
        }
        else {
            throw new Error(`asBooleans() was called; however, outputType() is ${this.outputType()}, not Type.Booleans.`);
        }
    }
    asFloat32s() {
        if (this.outputType() === ModelOutputType.Float32s) {
            return this.result.data;
        }
        else {
            throw new Error(`asFloat32s() was called; however, outputType() is ${this.outputType()}, not Type.Float32s.`);
        }
    }
    asFloat64s() {
        if (this.outputType() === ModelOutputType.Float64s) {
            return this.result.data;
        }
        else {
            throw new Error(`asFloat64s() was called; however, outputType() is ${this.outputType()}, not Type.Float64s.`);
        }
    }
    asInt8s() {
        if (this.outputType() === ModelOutputType.Int8s) {
            return this.result.data;
        }
        else {
            throw new Error(`asInt8s() was called; however, outputType() is ${this.outputType()}, not Type.Int8s.`);
        }
    }
    asInt16s() {
        if (this.outputType() === ModelOutputType.Int16s) {
            return this.result.data;
        }
        else {
            throw new Error(`asInt16s() was called; however, outputType() is ${this.outputType()}, not Type.Int16s.`);
        }
    }
    asInt32s() {
        if (this.outputType() === ModelOutputType.Int32s) {
            return this.result.data;
        }
        else {
            throw new Error(`asInt32s() was called; however, outputType() is ${this.outputType()}, not Type.Int32s.`);
        }
    }
    asStrings() {
        if (this.outputType() === ModelOutputType.Strings) {
            return this.result.data;
        }
        else {
            throw new Error(`asStrings() was called; however, outputType() is ${this.outputType()}, not Type.Strings.`);
        }
    }
    asUnsignedInt8s() {
        if (this.outputType() === ModelOutputType.UnsignedInt8s) {
            return this.result.data;
        }
        else {
            throw new Error(`asUnsignedInt8s() was called; however, outputType() is ${this.outputType()}, not Type.UnsignedInt8s.`);
        }
    }
    asUnsignedInt16s() {
        if (this.outputType() === ModelOutputType.UnsignedInt16s) {
            return this.result.data;
        }
        else {
            throw new Error(`asUnsignedInt16s() was called; however, outputType() is ${this.outputType()}, not Type.UnsignedInt16s.`);
        }
    }
    asUnsignedInt32s() {
        if (this.outputType() === ModelOutputType.UnsignedInt32s) {
            return this.result.data;
        }
        else {
            throw new Error(`asUnsignedInt32s() was called; however, outputType() is ${this.outputType()}, not Type.UnsignedInt32s.`);
        }
    }
}
//# sourceMappingURL=model.js.map